// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { Size } from "@quartzds/core";
import type { ComponentInterface } from "@stencil/core";
import { Component, h, Host, Prop } from "@stencil/core";

export type Width = "compact" | "standard";

@Component({
  tag: "qds-segmented-item",
  styleUrl: "segmented-item.css",
  shadow: true,
})
export class SegmentedItem implements ComponentInterface {
  /**
   * Prevents the segmented item from being interacted with: it cannot be
   * selected or focused.
   */
  @Prop() public readonly disabled?: boolean = false;

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary?: string = "default";

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string;

  /**
   * Whether or not the segmented item is selected.
   */
  @Prop() public readonly selected?: boolean = false;

  /**
   * The segmented-item's size.
   */
  @Prop() public readonly size?: Size = "standard";

  /**
   * The segmented-item's text.
   */
  @Prop() public readonly text: string;

  /**
   * The segmented-item's only shows an icon.
   */
  @Prop() public readonly iconOnly?: boolean = false;

  private get computedDisabled(): boolean {
    return this.disabled ?? false;
  }

  private get computedIconOnly(): boolean {
    return this.iconOnly ?? false;
  }

  private get computedSelected(): boolean {
    return this.selected ?? false;
  }

  public render() {
    return (
      <Host
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(this.computedIconOnly && {
          id: this.getId(),
        })}
      >
        <qds-button
          aria-current={this.computedSelected ? "true" : undefined}
          aria-label={this.computedIconOnly ? this.text : undefined}
          data-selected={this.selected}
          disabled={this.computedDisabled}
          icon-library={this.iconLibrary}
          icon-name={this.iconName}
          text={this.text}
          size={this.size}
          icon-only={this.computedIconOnly || undefined}
        />
        {this.computedIconOnly && !this.computedDisabled && (
          <qds-tooltip target={`#${this.getId()}`}>
            <p>{this.text}</p>
          </qds-tooltip>
        )}
      </Host>
    );
  }

  private getId(): string {
    return `${this.text}-${this.iconName}`.replace(" ", "");
  }
}
